/**
 * Auth Context for managing user authentication state
 * Uses sessionStorage for per-tab session isolation
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session (sessionStorage is per-tab, no cross-tab sharing)
    const storedUser = api.getUser();
    const hasToken = api.isAuthenticated();
    
    if (storedUser && hasToken) {
      setUser(storedUser);
      // Refresh user data - but don't clear auth on network errors
      api.getProfile().then(data => {
        if (data?.success) {
          setUser(data.data);
          api.setUser(data.data);
        }
        // If profile fetch fails but we have tokens, keep the user logged in
        // Only clear on explicit 401 errors (handled in api.request)
      }).catch((err) => {
        // Only clear tokens if it's an auth error (401)
        // Network errors or other issues should not log user out
        console.warn('Profile fetch failed:', err);
      });
    } else if (hasToken && !storedUser) {
      // Token exists but no user data - try to fetch profile
      api.getProfile().then(data => {
        if (data?.success) {
          setUser(data.data);
          api.setUser(data.data);
        }
      }).catch(() => {});
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    if (data?.success) {
      setUser(data.data.user);
    }
    return data;
  };

  const requestPhoneLoginCode = async (phone) => {
    return api.requestPhoneLoginCode(phone);
  };

  const verifyPhoneLoginCode = async (phone, code) => {
    const data = await api.verifyPhoneLoginCode(phone, code);
    if (data?.success) {
      setUser(data.data.user);
    }
    return data;
  };

  const register = async (email, password, confirmPassword, fullName, phone, smsConsent = false, confirmEmail = '') => {
    const data = await api.register(email, password, confirmPassword, fullName, phone, smsConsent, confirmEmail);
    if (data?.success) {
      setUser(data.data.user);
    }
    return data;
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (e) {
      // Ignore - tokens are cleared regardless
    } finally {
      setUser(null);
      // Clear per-buyer coupon state so it doesn't leak to the next user.
      localStorage.removeItem('appliedCoupon');
      localStorage.removeItem('upsellWindow');
      localStorage.removeItem('upsellAmpCoupon');
      localStorage.removeItem('upsellPassCoupon');
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    api.setUser(userData);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    requestPhoneLoginCode,
    verifyPhoneLoginCode,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
